import React, { useState } from "react";
import api, { apiError } from "@/lib/api";
import { PageHeader, Panel, EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CloudSun, MagnifyingGlass, Wind, Drop, Thermometer, CloudRain } from "@phosphor-icons/react";
import { toast } from "sonner";

const WMO = {
  0: ["Clear sky", "☀️"], 1: ["Mainly clear", "🌤️"], 2: ["Partly cloudy", "⛅"], 3: ["Overcast", "☁️"],
  45: ["Fog", "🌫️"], 48: ["Rime fog", "🌫️"], 51: ["Light drizzle", "🌦️"], 53: ["Drizzle", "🌦️"],
  55: ["Dense drizzle", "🌧️"], 61: ["Light rain", "🌦️"], 63: ["Rain", "🌧️"], 65: ["Heavy rain", "🌧️"],
  71: ["Light snow", "🌨️"], 73: ["Snow", "🌨️"], 75: ["Heavy snow", "❄️"], 80: ["Rain showers", "🌦️"],
  81: ["Showers", "🌧️"], 82: ["Violent showers", "⛈️"], 95: ["Thunderstorm", "⛈️"], 96: ["Thunderstorm + hail", "⛈️"],
};
const desc = (c) => (WMO[c] ? WMO[c][0] : "Unknown");
const emo = (c) => (WMO[c] ? WMO[c][1] : "🌡️");

export default function Weather() {
  const [city, setCity] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const search = async (e) => {
    e.preventDefault();
    if (!city.trim()) return;
    setLoading(true);
    try {
      const res = await api.get(`/weather?city=${encodeURIComponent(city)}`);
      setData(res.data);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Weather unavailable");
      setData(null);
    } finally { setLoading(false); }
  };

  const cur = data?.current;
  const daily = data?.daily;

  return (
    <div>
      <PageHeader title="Weather" subtitle="Check conditions for route and pickup/delivery planning" />

      <form onSubmit={search} className="flex gap-2 max-w-md mb-6">
        <div className="relative flex-1">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <Input data-testid="weather-city" value={city} onChange={(e) => setCity(e.target.value)} placeholder="City, e.g. Chicago, Dallas…" className="pl-9 bg-white border-slate-200" />
        </div>
        <Button type="submit" data-testid="weather-search" disabled={loading} className="bg-blue-600 text-white hover:bg-blue-700 font-semibold">{loading ? "…" : "Check"}</Button>
      </form>

      {!data ? (
        <Panel><EmptyState icon={CloudSun} title="Search a city" hint="Enter a city to view current conditions and a 5-day forecast." /></Panel>
      ) : (
        <div className="space-y-6">
          <Panel className="p-6">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <p className="text-sm text-slate-500">{data.location}</p>
                <div className="flex items-end gap-3 mt-1">
                  <span className="text-5xl font-heading font-black text-slate-900 tabular">{Math.round(cur?.temperature_2m)}°F</span>
                  <span className="text-4xl">{emo(cur?.weather_code)}</span>
                </div>
                <p className="text-slate-600 mt-1">{desc(cur?.weather_code)}</p>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <Metric icon={Thermometer} label="Feels like" value={`${Math.round(cur?.apparent_temperature)}°`} />
                <Metric icon={Drop} label="Humidity" value={`${cur?.relative_humidity_2m}%`} />
                <Metric icon={Wind} label="Wind" value={`${Math.round(cur?.wind_speed_10m)} mph`} />
              </div>
            </div>
          </Panel>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {daily?.time?.map((t, i) => (
              <Panel key={t} className="p-4 text-center animate-fade-up" style={{ animationDelay: `${i * 50}ms` }} data-testid={`forecast-${i}`}>
                <p className="text-xs text-slate-500 uppercase tracking-wide">{new Date(t).toLocaleDateString(undefined, { weekday: "short" })}</p>
                <div className="text-3xl my-2">{emo(daily.weather_code[i])}</div>
                <p className="text-sm font-mono text-slate-900">{Math.round(daily.temperature_2m_max[i])}° <span className="text-slate-500">{Math.round(daily.temperature_2m_min[i])}°</span></p>
                <p className="text-[11px] text-blue-400 mt-1 flex items-center justify-center gap-1"><CloudRain size={12} />{daily.precipitation_probability_max[i]}%</p>
              </Panel>
            ))}
          </div>
        </div>
      )}

      <div className="mt-6">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-heading font-bold text-slate-900 flex items-center gap-2"><CloudSun size={18} weight="duotone" className="text-blue-600" />Live Snow & Weather Radar</h3>
          <a href="https://www.ventusky.com/snow-cover-map/total#p=38.8;-98.1;3" target="_blank" rel="noreferrer" className="text-xs font-semibold text-blue-600 hover:text-blue-700" data-testid="ventusky-open">Open full map ↗</a>
        </div>
        <Panel className="p-0 overflow-hidden" data-testid="ventusky-panel">
          <iframe
            title="Ventusky Live Weather Map"
            src="https://www.ventusky.com/snow-cover-map/total#p=38.8;-98.1;3"
            className="w-full h-[520px] border-0 block"
            loading="lazy"
            allowFullScreen
          />
        </Panel>
      </div>
    </div>
  );
}

function Metric({ icon: Icon, label, value }) {
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-md p-3 text-center min-w-[90px]">
      <Icon size={18} className="text-blue-600 mx-auto" weight="duotone" />
      <p className="text-lg font-mono font-semibold text-slate-900 mt-1">{value}</p>
      <p className="text-[10px] text-slate-500 uppercase">{label}</p>
    </div>
  );
}
